import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Sidebar from "../Components/Sidebar";
import Navbar from "../Components/Navbar";
import Footer from "../Components/Footer";
import { Toaster, toast } from "react-hot-toast";
import {
  fetchAllowedMenus,
  fetchStateData,
  fetchDistrictData,
  fetchCityData,
  fetchMeadiaSourceData,
  fetchAllSubMediaSource,
  fetchUserData,
  User,
} from "../Services/APIService";
import CollapsibleSidebar from "../Components/CollapsibleSidebar";

interface MenuItem {
  menuID: number;
  menuName: string;
  menuUrl: string;
  icon: string;
  subMenuList?: [];
}

interface State {
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface District {
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface City {
  cityId: number;
  cityName: string;
  districtId: number;
  districtName: string;
  stateId: number;
  stateName: string;
  isActive: boolean;
}

interface MediaSource {
  sourceId: number;
  sourceName: string;
  isActive: boolean;
}

interface SubMediaSource {
  mediaSourceID: number;
  name: string;
  id: number;
  isActive: boolean;
}

interface Lead {
  leadId: number;
  firstName: string;
  lastName: string;
  gender: string;
  dob: string;
  dom: string;
  mobile: string;
  email: string;
  address: string;
  stateId: number;
  districtId: number;
  cityId: number;
  zipCode: number;
  sourceId: number;
  subSourceId: number;
  currentStatus: number;
  incomeFrom: number;
  incomeTo: number;
  initialAssignedUser: number;
  currentAssignedUser: number;
  tenantId: number;
  modifiedBy: number;
  assignType: string;
}

interface ValidationErrors {
  firstName?: string;
  lastName?: string;
  gender?: string;
  dob?: string;
  dom?: string;
  mobile?: string;
  email?: string;
  address?: string;
  stateId?: string;
  districtId?: string;
  cityId?: string;
  zipCode?: string;
  sourceId?: string;
  subSourceId?: string;
  incomeFrom?: string;
  incomeTo?: string;
  submitError?: string;
  assignType?: string;
  initialAssignedUser?: string;
}

const EditLead: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [allowedMenus, setAllowedMenus] = useState<MenuItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [states, setStates] = useState<State[]>([]);
  const [allDistricts, setAllDistricts] = useState<District[]>([]);
  const [filteredDistricts, setFilteredDistricts] = useState<District[]>([]);
  const [allCities, setAllCities] = useState<City[]>([]);
  const [filteredCities, setFilteredCities] = useState<City[]>([]);
  const [mediaSource, setMediaSource] = useState<MediaSource[]>([]);
  const [subMediaSource, setSubMediaSource] = useState<SubMediaSource[]>([]);
  const [filteredSubMediaSource, setFilteredSubMediaSource] = useState<
    SubMediaSource[]
  >([]);
  const [activeTab, setActiveTab] = useState("home");

  const [leadData, setLeadData] = useState<Lead | null>(null);
  const [assignedUser, setAssignedUser] = useState<User[]>([]);
  const [selectedAsignUserId, setSelectedAssignUserId] = useState<
    number | null
  >(null);
  const [assignType, setAssignType] = useState("assign");

  const toggleSidebar = () => {
    setIsSidebarCollapsed(!isSidebarCollapsed);
  };

  // Fetch menus and other data
  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const [
          menus,
          stateData,
          districtData,
          cityData,
          mediaSourceData,
          subMediaSourceData,
          assignUserData,
        ] = await Promise.all([
          fetchAllowedMenus(),
          fetchStateData(),
          fetchDistrictData(),
          fetchCityData(),
          fetchMeadiaSourceData(),
          fetchAllSubMediaSource(),
          fetchUserData(),
        ]);

        setAllowedMenus(menus);
        setStates(stateData);
        setAllDistricts(districtData);
        setAllCities(cityData);
        setMediaSource(mediaSourceData);
        setSubMediaSource(subMediaSourceData);
        setAssignedUser(assignUserData);

        const menuID = location.state?.menuID;
        const previousMenuID = localStorage.getItem("activeMenuID");
        if (menuID !== previousMenuID || menuID === null) {
          localStorage.setItem("activeMenuID", menuID);
        } else {
          localStorage.removeItem("activeMenuID");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load required data");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load lead data from navigation state
  useEffect(() => {
    const loadLeadData = async () => {
      try {
        const leadInfo = location.state?.LeadData;
        if (leadInfo) {
          setLeadData({
            ...leadInfo,
            dob: leadInfo.dob
              ? new Date(leadInfo.dob).toISOString().slice(0, 16)
              : "",
            dom: leadInfo.dom
              ? new Date(leadInfo.dom).toISOString().slice(0, 16)
              : "",
            zipCode: leadInfo.zipCode || "",
            incomeFrom: leadInfo.incomeFrom || "",
            incomeTo: leadInfo.incomeTo || "",
          });
        } else {
          throw new Error("Lead data not found");
        }
      } catch (error) {
        console.error("Error loading lead data:", error);
        toast.error("Failed to load lead data");
        navigate("/leadview");
      }
    };

    loadLeadData();
  }, [location, navigate]);

  useEffect(() => {
    if (leadData) {
      const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
      const assignedUserId = leadData.initialAssignedUser;

      console.log("Current User ID:", currentUserId);
      console.log("Assigned User ID:", assignedUserId);
      console.log("User IDs match:", currentUserId === assignedUserId);

      // If current user ID doesn't match assigned user ID, select "assign"
      if (
        currentUserId !== assignedUserId &&
        currentUserId !== 0 &&
        assignedUserId !== 0
      ) {
        console.log("Setting assign type to 'assign' - user IDs don't match");
        setLeadData((prev) =>
          prev ? { ...prev, assignType: "assign" } : null
        );
        setAssignType("assign");
        setSelectedAssignUserId(assignedUserId); // Keep the current assigned user selected
      } else if (currentUserId === assignedUserId && currentUserId !== 0) {
        console.log("Setting assign type to 'self' - user IDs match");
        setLeadData((prev) => (prev ? { ...prev, assignType: "self" } : null));
        setAssignType("self");
        setSelectedAssignUserId(currentUserId);
      } else {
        // Default case when user IDs are 0 or invalid
        console.log("Default case - setting to 'assign'");
        setLeadData((prev) =>
          prev ? { ...prev, assignType: "assign" } : null
        );
        setAssignType("assign");
      }
    }
  }, [leadData?.currentAssignedUser, leadData?.initialAssignedUser]);

  // Update the handleChange function to properly handle assignType changes
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >
  ) => {
    const { name, value } = e.target;
    const numberFields = [
      "zipCode",
      "incomeFrom",
      "incomeTo",
      "stateId",
      "districtId",
      "cityId",
      "sourceId",
      "subSourceId",
    ];

    // Special handling for assignType
    if (name === "assignType") {
      console.log("Assign type changed to:", value);
      setAssignType(value);

      if (value === "self") {
        const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
        console.log("Self selected - setting user ID to:", currentUserId);
        setSelectedAssignUserId(currentUserId);
        // Update both initialAssignedUser and currentAssignedUser for self assignment
        setLeadData((prev) =>
          prev
            ? {
                ...prev,
                assignType: value,
                initialAssignedUser: currentUserId,
                currentAssignedUser: currentUserId,
              }
            : null
        );
      } else {
        // For assign type, keep the current assigned user or clear if none
        console.log("Assign selected - keeping current assignment");
        const currentAssignedUser =
          leadData?.currentAssignedUser || leadData?.initialAssignedUser;
        setSelectedAssignUserId(currentAssignedUser || null);
        setLeadData((prev) => (prev ? { ...prev, assignType: value } : null));
      }
    } else {
      const parsedValue = numberFields.includes(name)
        ? value === ""
          ? ""
          : Number(value)
        : value;

      setLeadData((prev) => (prev ? { ...prev, [name]: parsedValue } : null));
    }

    if (errors[name as keyof ValidationErrors]) {
      setErrors({
        ...errors,
        [name]: undefined,
      });
    }
  };

  // Add this helper function to check user assignment status
  const checkUserAssignmentStatus = () => {
    const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
    const assignedUserId = leadData?.initialAssignedUser || 0;
    console.log(leadData);
    return {
      currentUserId,
      assignedUserId,
      isCurrentUserAssigned:
        currentUserId === assignedUserId && currentUserId !== 0,
      isValidComparison: currentUserId !== 0 && assignedUserId !== 0,
    };
  };
  // Filter districts based on state selection
  useEffect(() => {
    if (leadData?.stateId && allDistricts.length > 0) {
      const districts = allDistricts.filter(
        (district) => district.stateId === leadData.stateId
      );
      setFilteredDistricts(districts);
    } else {
      setFilteredDistricts([]);
    }
  }, [leadData?.stateId, allDistricts]);

  // Filter cities based on district selection
  useEffect(() => {
    if (leadData?.districtId && allCities.length > 0) {
      const cities = allCities.filter(
        (city) => city.districtId === leadData.districtId
      );
      setFilteredCities(cities);
    } else {
      setFilteredCities([]);
    }
  }, [leadData?.districtId, allCities]);

  // Filter sub-media sources based on source selection
  useEffect(() => {
    if (leadData?.sourceId && subMediaSource.length > 0) {
      const sources = subMediaSource.filter(
        (src) => src.mediaSourceID === leadData.sourceId
      );
      setFilteredSubMediaSource(sources);
    } else {
      setFilteredSubMediaSource([]);
    }
  }, [leadData?.sourceId, subMediaSource]);

  // const handleChange = (
  //   e: React.ChangeEvent<
  //     HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
  //   >
  // ) => {
  //   const { name, value } = e.target;
  //   const numberFields = [
  //     "zipCode",
  //     "incomeFrom",
  //     "incomeTo",
  //     "stateId",
  //     "districtId",
  //     "cityId",
  //     "sourceId",
  //     "subSourceId",
  //   ];
  //    // Special handling for assignType
  // if (name === "assignType") {
  //   console.log("Assign type changed to:", value);
  //   setAssignType(value);

  //   if (value === "self") {
  //     const currentUserId = parseInt(localStorage.getItem("UserId") ?? "0");
  //     console.log("Self selected - setting user ID to:", currentUserId);
  //     setSelectedAssignUserId(currentUserId);
  //     // Update both initialAssignedUser and currentAssignedUser for self assignment
  //     setLeadData((prev) => prev ? {
  //       ...prev,
  //       assignType: value,
  //       initialAssignedUser: currentUserId,
  //       currentAssignedUser: currentUserId
  //     } : null);
  //   } else {
  //     // For assign type, keep the current assigned user or clear if none
  //     console.log("Assign selected - keeping current assignment");
  //     const currentAssignedUser = leadData?.currentAssignedUser || leadData?.initialAssignedUser;
  //     setSelectedAssignUserId(currentAssignedUser || null);
  //     setLeadData((prev) => prev ? { ...prev, assignType: value } : null);
  //   }
  //   const parsedValue = numberFields.includes(name)
  //     ? value === ""
  //       ? ""
  //       : Number(value)
  //     : value;
  // }
  //   setLeadData((prev) => (prev ? { ...prev, [name]: parsedValue } : null));

  //   if (errors[name as keyof ValidationErrors]) {
  //     setErrors({
  //       ...errors,
  //       [name]: undefined,
  //     });
  //   }
  // };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const validateMobile = (mobile: string): boolean => {
    const mobileRegex = /^\d+$/;
    return mobileRegex.test(mobile);
  };

  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};
    let isValid = true;

    if (!leadData?.firstName.trim()) {
      newErrors.firstName = "First name is required";
      isValid = false;
    }
    if (!leadData?.lastName.trim()) {
      newErrors.lastName = "Last name is required";
      isValid = false;
    }
    if (!leadData?.gender.trim()) {
      newErrors.gender = "Please select Gender";
      isValid = false;
    }
    // if (!leadData?.dob.trim()) {
    //   newErrors.dob = "Please enter DOB";
    //   isValid = false;
    // }
    // if (!leadData?.dom.trim()) {
    //   newErrors.dom = "Please enter DOM";
    //   isValid = false;
    // }
    if (!leadData?.mobile.trim()) {
      newErrors.mobile = "Mobile number is required";
      isValid = false;
    } else if (!validateMobile(leadData.mobile)) {
      newErrors.mobile = "Mobile number should contain only digits";
      isValid = false;
    }
    if (!leadData?.email.trim()) {
      newErrors.email = "Please enter valid email";
      isValid = false;
    } else if (!validateEmail(leadData.email)) {
      newErrors.email = "Invalid email format";
      isValid = false;
    }
    if (!leadData?.address.trim()) {
      newErrors.address = "Please enter address";
      isValid = false;
    }
    if (!leadData?.stateId) {
      newErrors.stateId = "Please select a State";
      isValid = false;
    }
    if (!leadData?.districtId) {
      newErrors.districtId = "Please select a District";
      isValid = false;
    }
    if (!leadData?.cityId) {
      newErrors.cityId = "Please select a City";
      isValid = false;
    }
    if (!leadData?.zipCode) {
      newErrors.zipCode = "Please enter Zip Code";
      isValid = false;
    }
    if (!leadData?.sourceId) {
      newErrors.sourceId = "Please select a Source";
      isValid = false;
    }
    // if (!leadData?.subSourceId) {
    //   newErrors.subSourceId = "Please select a Subsource";
    //   isValid = false;
    // }
    // if (!leadData?.incomeFrom) {
    //   newErrors.incomeFrom = "Please enter Income From";
    //   isValid = false;
    // }
    // if (!leadData?.incomeTo) {
    //   newErrors.incomeTo = "Please enter Income To";
    //   isValid = false;
    // }

    if (!leadData?.assignType) {
      newErrors.assignType = "Please select an Assign Type";
      isValid = false;
    }
    if (leadData?.assignType === "assign" && !leadData?.initialAssignedUser) {
      newErrors.initialAssignedUser = "Please select a user to assign";
      isValid = false;
    }
    setErrors(newErrors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || !leadData) {
      //toast.error("Please fix the errors in the form");
      return;
    }

    try {
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      const updateData = {
        leadId: leadData.leadId,
        firstName: leadData.firstName,
        lastName: leadData.lastName,
        gender: leadData.gender,
        dob: leadData.dob,
        dom: leadData.dom,
        mobile: leadData.mobile,
        email: leadData.email,
        address: leadData.address,
        stateId: leadData.stateId,
        districtId: leadData.districtId,
        cityId: leadData.cityId,
        zipCode: Number(leadData.zipCode) || 0,
        sourceId: leadData.sourceId,
        subSourceId: leadData.subSourceId,
        currentStatus: leadData.currentStatus,
        incomeFrom: Number(leadData.incomeFrom) || 0,
        incomeTo: Number(leadData.incomeTo) || 0,
        initialAssignedUser: leadData.initialAssignedUser,
        currentAssignedUser: leadData.initialAssignedUser,
        tenantId: parseInt(tenantId),
        modifiedBy: parseInt(userId),
      };

      console.log("Update Data:", updateData);
      const accessToken = localStorage.getItem("authToken");
      const response = await fetch(
        "https://rprocrmsasdevapi.rprocrm.com/api/Lead/UpdateLead",
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(updateData),
        }
      );

      const result = await response.json();

      if (result.isUpdated) {
        toast.success(result.message || "Lead updated successfully");
        setTimeout(() => {
          navigate("/leadview");
        }, 1000);
      } else {
        toast.error(result.message || "Failed to update lead");
      }
    } catch (error) {
      console.error("Error updating lead:", error);
      toast.error("An error occurred while updating the lead");
    }
  };

  const handleTabClick = (tabId: string) => {
    setActiveTab(tabId);
  };

  const requiredField = <span className="text-danger ms-1">*</span>;

  if (isLoading || !leadData) {
    return <div className="p-4 text-center">Loading lead data...</div>;
  }

  return (
    <div className="wrapper">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      {!isSidebarCollapsed && <CollapsibleSidebar />}
      <div className="main">
        <Navbar onToggleSidebar={toggleSidebar} />
        <div className="main-content-div">
          <div className="main-header-div"></div>
          <main className="content mainContent">
            <div className="container-fluid p-0"></div>
            <div className="card">
              <ul
                className="nav nav-tabs tabHeaderPadding cardHeaderHegiht"
                id="myTab"
                role="tablist"
                style={{ borderBottom: "1px solid #84556b" }}
              >
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "home" ? "active" : ""
                    }`}
                    id="home-tab"
                    onClick={() => handleTabClick("home")}
                    type="button"
                    role="tab"
                    aria-controls="home-tab-pane"
                    aria-selected={activeTab === "home"}
                  >
                    Lead Details
                  </button>
                </li>
                <li className="nav-item" role="presentation">
                  <button
                    className={`nav-link card-title tab-btn cardHeaderHegiht ${
                      activeTab === "files" ? "active" : ""
                    }`}
                    id="files-tab"
                    onClick={() => handleTabClick("files")}
                    type="button"
                    role="tab"
                    aria-controls="files-tab-pane"
                    aria-selected={activeTab === "files"}
                  >
                    Other Details
                  </button>
                </li>
              </ul>

              <div className="tab-content card-body" id="myTabContent">
                <div
                  className={`tab-pane fade ${
                    activeTab === "home" ? "show active" : ""
                  }`}
                  id="home-tab-pane"
                  role="tabpanel"
                  aria-labelledby="home-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="firstName" className="form-label">
                          First Name{requiredField}
                        </label>
                        <input
                          type="text"
                          id="firstName"
                          name="firstName"
                          className={`form-control form-control-lg ${
                            errors.firstName ? "is-invalid" : ""
                          }`}
                          placeholder="Enter First Name"
                          value={leadData.firstName}
                          onChange={handleChange}
                          required
                        />
                        {errors.firstName && (
                          <div className="invalid-feedback text-danger">
                            {errors.firstName}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="lastName" className="form-label">
                          Last Name{requiredField}
                        </label>
                        <input
                          type="text"
                          id="lastName"
                          name="lastName"
                          className={`form-control form-control-lg ${
                            errors.lastName ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Last Name"
                          value={leadData.lastName}
                          onChange={handleChange}
                          required
                        />
                        {errors.lastName && (
                          <div className="invalid-feedback text-danger">
                            {errors.lastName}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="gender" className="form-label">
                          Gender{requiredField}
                        </label>
                        <select
                          id="gender"
                          name="gender"
                          value={leadData.gender}
                          onChange={handleChange}
                          className={`form-control form-control-lg ${
                            errors.gender ? "is-invalid" : ""
                          }`}
                          required
                        >
                          <option value="">Select Gender</option>
                          <option value="Male">Male</option>
                          <option value="Female">Female</option>
                          <option value="Other">Other</option>
                        </select>
                        {errors.gender && (
                          <div className="invalid-feedback text-danger">
                            {errors.gender}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="dob" className="form-label">
                          DOB
                        </label>
                        <input
                          type="datetime-local"
                          id="dob"
                          name="dob"
                          className={`form-control form-control-lg ${
                            errors.dob ? "is-invalid" : ""
                          }`}
                          placeholder="Enter DOB"
                          value={leadData.dob}
                          onChange={handleChange}
                          required
                        />
                        {errors.dob && (
                          <div className="invalid-feedback text-danger">
                            {errors.dob}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="dom" className="form-label">
                          DOM
                        </label>
                        <input
                          type="datetime-local"
                          id="dom"
                          name="dom"
                          className={`form-control form-control-lg ${
                            errors.dom ? "is-invalid" : ""
                          }`}
                          placeholder="Enter DOM"
                          value={leadData.dom}
                          onChange={handleChange}
                          required
                        />
                        {errors.dom && (
                          <div className="invalid-feedback text-danger">
                            {errors.dom}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="mobile" className="form-label">
                          Mobile{requiredField}
                        </label>
                        <input
                          id="mobile"
                          name="mobile"
                          className={`form-control form-control-lg ${
                            errors.mobile ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Mobile No"
                          value={leadData.mobile}
                          onChange={handleChange}
                          required
                        />
                        {errors.mobile && (
                          <div className="invalid-feedback text-danger">
                            {errors.mobile}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="email" className="form-label">
                          Email{requiredField}
                        </label>
                        <input
                          type="text"
                          id="email"
                          name="email"
                          className={`form-control form-control-lg ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Email"
                          value={leadData.email}
                          onChange={handleChange}
                          required
                        />
                        {errors.email && (
                          <div className="invalid-feedback text-danger">
                            {errors.email}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="address" className="form-label">
                          Address{requiredField}
                        </label>
                        <textarea
                          id="address"
                          name="address"
                          className={`form-control form-control-lg ${
                            errors.address ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Address"
                          value={leadData.address}
                          onChange={handleChange}
                          required
                        />
                        {errors.address && (
                          <div className="invalid-feedback text-danger">
                            {errors.address}
                          </div>
                        )}
                      </div>
                    </div>
                  </form>
                </div>

                <div
                  className={`tab-pane fade ${
                    activeTab === "files" ? "show active" : ""
                  }`}
                  id="files-tab-pane"
                  role="tabpanel"
                  aria-labelledby="files-tab"
                  tabIndex={0}
                >
                  <form onSubmit={handleSubmit}>
                    <div className="row mb-3">
                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="stateId" className="form-label">
                          State{requiredField}
                        </label>
                        <select
                          id="stateId"
                          name="stateId"
                          className={`form-control form-control-lg ${
                            errors.stateId ? "is-invalid" : ""
                          }`}
                          value={leadData.stateId}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.stateId} value={state.stateId}>
                              {state.stateName}
                            </option>
                          ))}
                        </select>
                        {errors.stateId && (
                          <div className="invalid-feedback text-danger">
                            {errors.stateId}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="districtId" className="form-label">
                          District{requiredField}
                        </label>
                        <select
                          id="districtId"
                          name="districtId"
                          className={`form-control form-control-lg ${
                            errors.districtId ? "is-invalid" : ""
                          }`}
                          value={leadData.districtId}
                          onChange={handleChange}
                          required
                          disabled={!leadData.stateId}
                        >
                          <option value="">
                            {leadData.stateId
                              ? filteredDistricts.length > 0
                                ? "Select District"
                                : "No districts available"
                              : "Select state first"}
                          </option>
                          {filteredDistricts.map((district) => (
                            <option
                              key={district.districtId}
                              value={district.districtId}
                            >
                              {district.districtName}
                            </option>
                          ))}
                        </select>
                        {errors.districtId && (
                          <div className="invalid-feedback text-danger">
                            {errors.districtId}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="cityId" className="form-label">
                          City{requiredField}
                        </label>
                        <select
                          id="cityId"
                          name="cityId"
                          className={`form-control form-control-lg ${
                            errors.cityId ? "is-invalid" : ""
                          }`}
                          value={leadData.cityId}
                          onChange={handleChange}
                          required
                          disabled={!leadData.districtId}
                        >
                          <option value="">
                            {leadData.districtId
                              ? filteredCities.length > 0
                                ? "Select City"
                                : "No cities available"
                              : "Select district first"}
                          </option>
                          {filteredCities.map((city) => (
                            <option key={city.cityId} value={city.cityId}>
                              {city.cityName}
                            </option>
                          ))}
                        </select>
                        {errors.cityId && (
                          <div className="invalid-feedback text-danger">
                            {errors.cityId}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="zipCode" className="form-label">
                          Zipcode{requiredField}
                        </label>
                        <input
                          type="text"
                          id="zipCode"
                          name="zipCode"
                          className={`form-control form-control-lg ${
                            errors.zipCode ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Zipcode"
                          value={leadData.zipCode}
                          onChange={handleChange}
                          required
                        />
                        {errors.zipCode && (
                          <div className="invalid-feedback text-danger">
                            {errors.zipCode}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="sourceId" className="form-label">
                          Source{requiredField}
                        </label>
                        <select
                          id="sourceId"
                          name="sourceId"
                          className={`form-control form-control-lg ${
                            errors.sourceId ? "is-invalid" : ""
                          }`}
                          value={leadData.sourceId}
                          onChange={handleChange}
                          required
                        >
                          <option value="">Select Source</option>
                          {mediaSource.map((src) => (
                            <option key={src.sourceId} value={src.sourceId}>
                              {src.sourceName}
                            </option>
                          ))}
                        </select>
                        {errors.sourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.sourceId}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="subSourceId" className="form-label">
                          Sub Source{requiredField}
                        </label>
                        <select
                          id="subSourceId"
                          name="subSourceId"
                          className={`form-control form-control-lg ${
                            errors.subSourceId ? "is-invalid" : ""
                          }`}
                          value={leadData.subSourceId}
                          onChange={handleChange}
                          required
                        >
                          <option value="">
                            {leadData.sourceId
                              ? filteredSubMediaSource.length > 0
                                ? "Select Sub Source"
                                : "No sub source available"
                              : "Select source first"}
                          </option>
                          {filteredSubMediaSource.map((ssrc) => (
                            <option key={ssrc.id} value={ssrc.id}>
                              {ssrc.name}
                            </option>
                          ))}
                        </select>
                        {errors.subSourceId && (
                          <div className="invalid-feedback text-danger">
                            {errors.subSourceId}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="incomeFrom" className="form-label">
                          Income From
                        </label>
                        <input
                          type="text"
                          id="incomeFrom"
                          name="incomeFrom"
                          className={`form-control form-control-lg ${
                            errors.incomeFrom ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Income From"
                          value={leadData.incomeFrom}
                          onChange={handleChange}
                          required
                        />
                        {errors.incomeFrom && (
                          <div className="invalid-feedback text-danger">
                            {errors.incomeFrom}
                          </div>
                        )}
                      </div>

                      <div className="col-12 col-lg-6 mb-3">
                        <label htmlFor="incomeTo" className="form-label">
                          Income To
                        </label>
                        <input
                          type="text"
                          id="incomeTo"
                          name="incomeTo"
                          className={`form-control form-control-lg ${
                            errors.incomeTo ? "is-invalid" : ""
                          }`}
                          placeholder="Enter Income To"
                          value={leadData.incomeTo}
                          onChange={handleChange}
                          required
                        />
                        {errors.incomeTo && (
                          <div className="invalid-feedback text-danger">
                            {errors.incomeTo}
                          </div>
                        )}
                      </div>
                      <div className="col-12 col-lg-6 mb-3">
                        <label className="form-label d-block mb-2">
                          Assign Type{requiredField}
                        </label>
                        {/* <div className="form-check form-check-inline">
    <input
      className="form-check-input"
      type="radio"
      id="assign"
      name="assignType"
      value="assign"
      checked={leadData.assignType === "assign"}
      onChange={handleChange}
    />
    <label className="form-check-label" htmlFor="assign">Assign</label>
  </div>
  <div className="form-check form-check-inline">
    <input
      className="form-check-input"
      type="radio"
      id="self"
      name="assignType"
      
      value="self"
      checked={leadData.assignType === "self"}
      onChange={() => {
        setAssignType("self");
        setSelectedAssignUserId(parseInt(localStorage.getItem("UserId") ?? "0")); // Auto-assign on Self
      }}
    />
    <label className="form-check-label" htmlFor="self">Self</label>
  </div>
   */}

                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="assign"
                            name="assignType"
                            value="assign"
                            checked={assignType === "assign"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label" htmlFor="assign">
                            Assign
                          </label>
                        </div>
                        <div className="form-check form-check-inline">
                          <input
                            className="form-check-input"
                            type="radio"
                            id="self"
                            name="assignType"
                            value="self"
                            checked={assignType === "self"}
                            onChange={handleChange}
                          />
                          <label className="form-check-label" htmlFor="self">
                            Self
                          </label>
                          {errors.assignType && (
                            <div className="invalid-feedback text-danger d-block">
                              {errors.assignType}
                            </div>
                          )}
                        </div>
                      </div>

                      {leadData.assignType === "assign" && (
                        <div className="col-12 col-lg-6 mb-3">
                          <label
                            htmlFor="initialAssignedUser"
                            className="form-label"
                          >
                            Assign to User{requiredField}
                          </label>
                          <select
                            id="initialAssignedUser"
                            name="initialAssignedUser"
                            className={`form-control form-control-lg ${
                              errors.address ? "is-invalid" : ""
                            }`}
                            value={leadData.initialAssignedUser ?? ""}
                            onChange={(e) => {
                              setSelectedAssignUserId(parseInt(e.target.value));
                              handleChange(e);
                            }}
                            required
                          >
                            <option value="">Select User</option>
                            {assignedUser.map((usr) => (
                              <option key={usr.userId} value={usr.userId}>
                                {usr.userName}
                              </option>
                            ))}
                          </select>

                          {errors.initialAssignedUser && (
                            <div className="invalid-feedback text-danger">
                              {errors.initialAssignedUser}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </form>
                </div>

                <div>
                  {activeTab === "files" && (
                    <>
                      <button
                        type="submit"
                        className="btn btn-primary me-2 mt-4"
                        onClick={handleSubmit}
                      >
                        Update Lead
                      </button>
                      <button
                        type="button"
                        className="btn btn-secondary mt-4"
                        onClick={() =>
                          navigate(location.state?.redirectTo || "/leadview", {
                            state: { leadId: leadData?.leadId },
                          })
                        }
                      >
                        <i className="bi bi-arrow-return-left"></i>{" "}
                        {location.state?.redirectTo === "/leaddetails"
                          ? "Back To Lead Details"
                          : "Back To List"}
                      </button>
                    </>
                  )}
                  {activeTab === "home" && (
                    <button
                      type="button"
                      className="btn btn-secondary mt-4"
                      onClick={() =>
                        navigate(location.state?.redirectTo || "/leadview", {
                          state: { leadId: leadData?.leadId },
                        })
                      }
                    >
                      <i className="bi bi-arrow-return-left"></i>{" "}
                      {location.state?.redirectTo === "/leaddetails"
                        ? "Back To Lead Details"
                        : "Back To List"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </main>
        </div>
        <Footer />
      </div>
    </div>
  );
};

export default EditLead;
